import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import Spinner from '../components/Spinner';

// Raf Stoku: rafta gerçekten duran adet (Ozon'a girilen stoktan ayrı).
// Siparişler bakiyeyi düşer, eksiye inen ürünler alım listesine düşer.
// Kurallar: database/055_raf_stok.sql

const BASE = '/api';
function api(method, path, body) {
  const token = localStorage.getItem('ozon_token');
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { const e = new Error(d.error || r.statusText); e.status = r.status; throw e; }
    return d;
  });
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('tr-TR', {
    timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function beep(ok = true) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = ok ? 880 : 220;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (ok ? 0.12 : 0.25));
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + (ok ? 0.12 : 0.25));
  } catch { /* sessiz */ }
}

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
];

function CameraScanner({ onScan, onClose }) {
  const onScanRef = useRef(onScan);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  useEffect(() => {
    const scanner = new Html5Qrcode('raf-qr-reader', { formatsToSupport: SUPPORTED_FORMATS, verbose: false });
    let bitti = false;
    scanner.start({ facingMode: 'environment' }, { fps: 25 }, (text) => {
      if (bitti) return;
      bitti = true;
      if (navigator.vibrate) navigator.vibrate([80]);
      onScanRef.current(text);
    }, () => {}).catch(e => console.warn('[RafScanner]', e));
    return () => { scanner.isScanning && scanner.stop().catch(() => {}); };
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3 bg-black/70">
        <p className="text-white text-sm font-semibold">Ürün veya koli barkodunu tut</p>
        <button onClick={onClose} className="text-white bg-white/20 rounded-xl px-4 py-2 text-sm font-semibold">✕ Kapat</button>
      </div>
      <div id="raf-qr-reader" className="flex-1 w-full" />
    </div>
  );
}

// Ürün arama kutusu (barkod tanımlarken)
function UrunSec({ urunler, onSec }) {
  const [ara, setAra] = useState('');
  const sonuc = useMemo(() => {
    const t = ara.trim().toLowerCase();
    if (t.length < 2) return [];
    return urunler.filter(u =>
      u.name?.toLowerCase().includes(t) || u.sku.includes(t) || u.offer_ids.some(o => o.toLowerCase().includes(t))
    ).slice(0, 8);
  }, [ara, urunler]);
  return (
    <div>
      <input autoFocus value={ara} onChange={e => setAra(e.target.value)}
        placeholder="Ürün adı, offer_id veya SKU ile ara…"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      {sonuc.length > 0 && (
        <div className="mt-1 border border-gray-200 rounded-lg divide-y max-h-64 overflow-y-auto">
          {sonuc.map(u => (
            <button key={u.sku} onClick={() => onSec(u)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2">
              {u.image_url && <img src={u.image_url} alt="" className="w-8 h-8 object-cover rounded" />}
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-gray-900 truncate">{u.name}</span>
                <span className="block text-xs text-gray-400 truncate">{u.offer_ids.join(' · ')} · SKU {u.sku}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Okutulan barkod tanımlı değilse: hangi ürün, tekli mi koli mi
function BarkodTanimla({ barkod, urunler, onKaydet, onIptal }) {
  const [urun, setUrun] = useState(null);
  const [koli, setKoli] = useState(false);
  const [koliIci, setKoliIci] = useState('');
  const [hata, setHata] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);

  async function kaydet() {
    const adet = koli ? parseInt(koliIci, 10) : 1;
    if (!urun) return setHata('Ürün seç');
    if (koli && (!Number.isInteger(adet) || adet < 2)) return setHata('Koli içi adet 2 veya daha fazla olmalı');
    setKaydediliyor(true); setHata('');
    try {
      onKaydet(await api('POST', '/raf-stok/barkod', { barkod, sku: urun.sku, koli_ici_adet: adet }), urun);
    } catch (e) { setHata(e.message); setKaydediliyor(false); }
  }

  return (
    <div className="border border-amber-300 bg-amber-50 rounded-xl p-4 space-y-3">
      <p className="text-sm text-amber-900">
        <b>{barkod}</b> barkodu tanımlı değil. Bir kere tanımla, sonraki okutmalarda tanır.
      </p>
      {urun ? (
        <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border">
          <span className="flex-1 text-sm">{urun.name}</span>
          <button onClick={() => setUrun(null)} className="text-xs text-blue-600">değiştir</button>
        </div>
      ) : <UrunSec urunler={urunler} onSec={setUrun} />}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={!koli} onChange={() => setKoli(false)} /> Tekli ürün barkodu
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={koli} onChange={() => setKoli(true)} /> Koli barkodu, içinde
          <input type="number" min="2" value={koliIci} onChange={e => { setKoli(true); setKoliIci(e.target.value); }}
            className="w-20 border border-gray-300 rounded px-2 py-1" /> adet
        </label>
      </div>
      {hata && <p className="text-sm text-red-600">{hata}</p>}
      <div className="flex gap-2">
        <button onClick={kaydet} disabled={kaydediliyor}
          className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">Tanımla</button>
        <button onClick={onIptal} className="text-sm text-gray-600 px-4 py-2">Vazgeç</button>
      </div>
    </div>
  );
}

// Barkodu tanınan ürün için giriş / sayım formu
function IslemFormu({ mod, tanim, onBitti, onIptal }) {
  const koliIci = tanim.koli_ici_adet;
  const [koliSayisi, setKoliSayisi] = useState(koliIci > 1 ? '1' : '');
  const [tekli, setTekli] = useState(koliIci > 1 ? '' : (mod === 'giris' ? '1' : ''));
  const [hata, setHata] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const toplam = (parseInt(koliSayisi, 10) || 0) * (koliIci > 1 ? koliIci : 0) + (parseInt(tekli, 10) || 0);

  async function kaydet() {
    setHata('');
    if (mod === 'giris' && toplam <= 0) return setHata('Adet 0\'dan büyük olmalı');
    if (mod === 'sayim' && (koliSayisi === '' && tekli === '')) return setHata('Sayılan adedi gir');
    setKaydediliyor(true);
    try {
      const r = mod === 'giris'
        ? (koliIci > 1 && !(parseInt(tekli, 10) > 0)
            ? await api('POST', '/raf-stok/giris', { barkod: tanim.barkod, koli_adet: parseInt(koliSayisi, 10) })
            : await api('POST', '/raf-stok/giris', { sku: tanim.sku, adet: toplam, aciklama: koliIci > 1 ? `${koliSayisi || 0} koli × ${koliIci} + ${tekli || 0} tekli` : null }))
        : await api('POST', '/raf-stok/sayim', { sku: tanim.sku, sayilan: toplam });
      onBitti(r);
    } catch (e) { setHata(e.message); setKaydediliyor(false); }
  }

  const enter = e => e.key === 'Enter' && kaydet();
  return (
    <div className={`border rounded-xl p-4 space-y-3 ${mod === 'giris' ? 'border-green-300 bg-green-50' : 'border-indigo-300 bg-indigo-50'}`}>
      <div>
        <p className="text-sm font-semibold text-gray-900">{tanim.name}</p>
        <p className="text-xs text-gray-500">
          {!tanim.barkod ? 'Barkodsuz, elle' : `${tanim.barkod} · ${koliIci > 1 ? `koli barkodu, koli içi ${koliIci} adet` : 'tekli ürün barkodu'}`}
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        {koliIci > 1 && (
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Koli sayısı</span>
            <input autoFocus type="number" min="0" value={koliSayisi} onChange={e => setKoliSayisi(e.target.value)} onKeyDown={enter}
              className="w-24 border border-gray-300 rounded-lg px-2 py-2 text-lg" />
          </label>
        )}
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">{koliIci > 1 ? '+ Tekli adet' : (mod === 'giris' ? 'Adet' : 'Rafta sayılan adet')}</span>
          <input autoFocus={koliIci <= 1} type="number" min="0" value={tekli} onChange={e => setTekli(e.target.value)} onKeyDown={enter}
            className="w-24 border border-gray-300 rounded-lg px-2 py-2 text-lg" />
        </label>
        <div className="text-gray-700 pb-2">= <b className="text-xl">{toplam}</b> adet</div>
      </div>
      {mod === 'sayim' && (
        <p className="text-xs text-indigo-800">
          Rafta gördüğün her şeyi say; henüz paketlenmemiş siparişlerin ürünleri de dahil. Sistem onları ayrıca düşer.
        </p>
      )}
      {hata && <p className="text-sm text-red-600">{hata}</p>}
      <div className="flex gap-2">
        <button onClick={kaydet} disabled={kaydediliyor}
          className={`text-white text-sm font-semibold px-5 py-2 rounded-lg disabled:opacity-50 ${mod === 'giris' ? 'bg-green-600' : 'bg-indigo-600'}`}>
          {mod === 'giris' ? 'Giriş yap' : 'Sayımı kaydet'}
        </button>
        <button onClick={onIptal} className="text-sm text-gray-600 px-4 py-2">Vazgeç</button>
      </div>
    </div>
  );
}

// Barkodsuz elle sayım / giriş (satırdaki butonlar)
function ElleModal({ urun, mod, onKapat, onBitti }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onKapat}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <IslemFormu mod={mod} tanim={{ sku: urun.sku, name: urun.name, barkod: null, koli_ici_adet: 1 }}
          onBitti={onBitti} onIptal={onKapat} />
      </div>
    </div>
  );
}

const TIP_ADI = { sayim: 'Sayım', siparis: 'Sipariş', iptal: 'İptal (geri)', giris: 'Giriş' };

function GecmisModal({ urun, onKapat, onBarkodSil }) {
  const [satirlar, setSatirlar] = useState(null);
  useEffect(() => {
    api('GET', `/raf-stok/hareket/${urun.sku}`).then(setSatirlar).catch(() => setSatirlar([]));
  }, [urun.sku]);
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onKapat}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-start gap-3">
          <div className="flex-1">
            <p className="font-semibold text-gray-900 text-sm">{urun.name}</p>
            <p className="text-xs text-gray-400">{urun.offer_ids.join(' · ')} · SKU {urun.sku}</p>
          </div>
          <button onClick={onKapat} className="text-gray-400 text-lg">✕</button>
        </div>
        {urun.barkodlar.length > 0 && (
          <div className="px-4 py-2 border-b flex flex-wrap gap-2 text-xs">
            {urun.barkodlar.map(b => (
              <span key={b.barkod} className="bg-gray-100 rounded px-2 py-1">
                {b.barkod} {b.koli_ici_adet > 1 ? `(koli ${b.koli_ici_adet})` : '(tekli)'}
                <button onClick={() => onBarkodSil(b.barkod)} className="ml-1 text-red-500" title="Barkodu sil">✕</button>
              </span>
            ))}
          </div>
        )}
        <div className="overflow-y-auto">
          {!satirlar ? <div className="p-6 flex justify-center"><Spinner /></div> : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 sticky top-0">
                <tr><th className="text-left px-3 py-2">Tarih</th><th className="text-left px-3 py-2">Hareket</th>
                  <th className="text-right px-3 py-2">Miktar</th><th className="text-right px-3 py-2">Bakiye</th>
                  <th className="text-left px-3 py-2">Ayrıntı</th></tr>
              </thead>
              <tbody className="divide-y">
                {satirlar.map(h => (
                  <tr key={h.id}>
                    <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">{formatDate(h.created_at)}</td>
                    <td className="px-3 py-1.5">{TIP_ADI[h.tip]}</td>
                    <td className={`px-3 py-1.5 text-right font-medium ${h.miktar < 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {h.miktar > 0 ? '+' : ''}{h.miktar}
                    </td>
                    <td className="px-3 py-1.5 text-right">{h.bakiye_sonra}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-500">
                      {h.tip === 'sayim' && `rafta ${h.sayilan} sayıldı`}
                      {h.posting_number}
                      {h.koli_adet && `${h.koli_adet} koli`}
                      {h.aciklama && ` ${h.aciklama}`}
                      {h.kullanici && ` · ${h.kullanici}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// Barkod eşleştirme: her ürüne ürün barkodu (koli içi boş/1) veya koli barkodu (koli içi > 1) tanımlanır.
// Okuyucuyla: satırdaki barkod kutusuna tıkla, okut; Enter ile kaydedilir.
function BarkodSatiri({ urun, onDegisti, onSil }) {
  const [barkod, setBarkod] = useState('');
  const [koliIci, setKoliIci] = useState('');
  const [hata, setHata] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const barkodRef = useRef(null);

  async function kaydet(tasi = false) {
    const b = barkod.trim();
    if (!b) return;
    const adet = koliIci === '' ? 1 : parseInt(koliIci, 10);
    if (!Number.isInteger(adet) || adet < 1) return setHata('Koli içi adet 1 veya daha fazla olmalı');
    setKaydediliyor(true); setHata('');
    try {
      await api('POST', '/raf-stok/barkod', { barkod: b, sku: urun.sku, koli_ici_adet: adet, tasi });
      beep(true);
      setBarkod(''); setKoliIci('');
      onDegisti();
      setTimeout(() => barkodRef.current?.focus(), 50);
    } catch (e) {
      if (e.status === 409 && window.confirm(`${e.message}\n\nBu ürüne taşınsın mı?`)) { setKaydediliyor(false); return kaydet(true); }
      beep(false); setHata(e.message);
    } finally { setKaydediliyor(false); }
  }

  const enter = e => e.key === 'Enter' && kaydet();
  return (
    <tr className="align-top">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          {urun.image_url && <img src={urun.image_url} alt="" className="w-9 h-9 object-cover rounded" />}
          <p className="text-gray-900 max-w-sm">{urun.name}</p>
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-gray-600">{urun.offer_ids.map(o => <div key={o}>{o}</div>)}</td>
      <td className="px-3 py-2 text-xs text-gray-600">{urun.sku}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {urun.barkodlar.length === 0 && <span className="text-xs text-gray-300">—</span>}
          {urun.barkodlar.map(b => (
            <span key={b.barkod} className={`text-xs rounded px-2 py-0.5 ${b.koli_ici_adet > 1 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
              {b.barkod} · {b.koli_ici_adet > 1 ? `koli ${b.koli_ici_adet}` : 'tekli'}
              <button onClick={() => onSil(b.barkod)} className="ml-1 text-red-500" title="Barkodu sil">✕</button>
            </span>
          ))}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <input ref={barkodRef} value={barkod} onChange={e => setBarkod(e.target.value)} onKeyDown={enter}
            placeholder="Barkod okut / yaz" className="w-40 border border-gray-300 rounded px-2 py-1 text-sm" />
          <input type="number" min="1" value={koliIci} onChange={e => setKoliIci(e.target.value)} onKeyDown={enter}
            placeholder="Koli içi" title="Boş = tekli ürün barkodu. Koli barkoduysa içindeki adet."
            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm" />
          <button onClick={() => kaydet()} disabled={kaydediliyor || !barkod.trim()}
            className="bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-40">Ekle</button>
        </div>
        {hata && <p className="text-xs text-red-600 mt-1">{hata}</p>}
      </td>
    </tr>
  );
}

function BarkodEslestirme({ urunler, onDegisti, onSil }) {
  const [filtre, setFiltre] = useState('barkodsuz');
  const [ara, setAra] = useState('');
  const sayac = {
    barkodsuz: urunler.filter(u => !u.barkodlar.length).length,
    barkodlu: urunler.filter(u => u.barkodlar.length).length,
    tumu: urunler.length,
  };
  const liste = useMemo(() => {
    let l = urunler;
    if (filtre === 'barkodsuz') l = l.filter(u => !u.barkodlar.length);
    if (filtre === 'barkodlu') l = l.filter(u => u.barkodlar.length);
    const t = ara.trim().toLowerCase();
    if (t) l = l.filter(u => u.name?.toLowerCase().includes(t) || u.sku.includes(t)
      || u.offer_ids.some(o => o.toLowerCase().includes(t)) || u.barkodlar.some(b => b.barkod.includes(t)));
    return l;
  }, [urunler, filtre, ara]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="p-3 border-b space-y-2">
        <p className="text-sm text-gray-600">
          Hangi barkod hangi ürün: ürün barkodu için koli içini boş bırak; koli barkodu için koli içindeki adedi yaz
          (ör. 24 → 2 koli okutulunca 48 girer). Bir ürüne birden fazla barkod tanımlanabilir.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {[['barkodsuz', 'Barkodu olmayan'], ['barkodlu', 'Barkodlu'], ['tumu', 'Tüm ürünler']].map(([k, ad]) => (
            <button key={k} onClick={() => setFiltre(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${filtre === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {ad} ({sayac[k]})
            </button>
          ))}
          <input value={ara} onChange={e => setAra(e.target.value)} placeholder="Ürün, offer_id, SKU, barkod ara…"
            className="ml-auto border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Ürün</th><th className="text-left px-3 py-2">offer_id</th>
              <th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Barkodlar</th>
              <th className="text-left px-3 py-2">Barkod ekle</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {liste.map(u => <BarkodSatiri key={u.sku} urun={u} onDegisti={onDegisti} onSil={onSil} />)}
          </tbody>
        </table>
        {liste.length === 0 && <p className="p-6 text-sm text-gray-500 text-center">Ürün yok.</p>}
      </div>
    </div>
  );
}

function csvIndir(satirlar) {
  const kac = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const baslik = ['Tür', 'Ürün', 'offer_id', 'SKU', 'Eksik adet', 'Bekleyen sipariş'];
  const govde = satirlar.map(u => [u.dropship ? 'Drop-ship' : 'Raf', u.name, u.offer_ids.join(' / '), u.sku, -u.bakiye, u.bekleyen_siparis]);
  const metin = '﻿' + [baslik, ...govde].map(r => r.map(kac).join(';')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([metin], { type: 'text/csv;charset=utf-8' }));
  a.download = `alim-listesi-${new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Moscow' })}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function RafStok() {
  const [urunler, setUrunler] = useState(null);
  const [hata, setHata] = useState('');
  const [mod, setMod] = useState('giris');           // okutma modu: giris | sayim
  const [gorunum, setGorunum] = useState('stok');    // stok | alim
  const [filtre, setFiltre] = useState('takipte');
  const [ara, setAra] = useState('');
  const [barkodGir, setBarkodGir] = useState('');
  const [okutulan, setOkutulan] = useState(null);    // { tanim } | { tanimsiz: barkod }
  const [bildirim, setBildirim] = useState(null);
  const [kamera, setKamera] = useState(false);
  const [elle, setElle] = useState(null);            // { urun, mod }
  const [gecmis, setGecmis] = useState(null);
  const inputRef = useRef(null);

  const yukle = useCallback(async () => {
    try { setUrunler(await api('GET', '/raf-stok')); setHata(''); }
    catch (e) { setHata(e.message); setUrunler(u => u || []); }
  }, []);
  useEffect(() => { yukle(); }, [yukle]);
  // Henüz takipte ürün yoksa liste boş görünmesin: tüm ürünlerle aç.
  const ilkYukleme = useRef(true);
  useEffect(() => {
    if (!urunler || !ilkYukleme.current) return;
    ilkYukleme.current = false;
    if (!urunler.some(u => u.takipte)) setFiltre('tumu');
  }, [urunler]);

  function bildir(tip, metin) {
    setBildirim({ tip, metin });
    setTimeout(() => setBildirim(null), 5000);
  }

  async function okut(kod) {
    const barkod = kod.trim();
    setBarkodGir('');
    if (!barkod) return;
    try {
      setOkutulan({ tanim: await api('GET', `/raf-stok/barkod/${encodeURIComponent(barkod)}`) });
      beep(true);
    } catch (e) {
      if (e.status === 404) { beep(false); setOkutulan({ tanimsiz: barkod }); }
      else bildir('hata', e.message);
    }
  }

  function bitti(r) {
    beep(true);
    const urun = urunler?.find(u => u.sku === String(r.sku));
    const ad = urun?.name || r.sku;
    const metin = r.sayilan !== undefined
      ? `${ad}: ${r.sayilan} sayıldı${r.rafta_siparis ? `, ${r.rafta_siparis} adedi paketlenmemiş siparişlerin` : ''} → bakiye ${r.bakiye} (fark ${r.fark > 0 ? '+' : ''}${r.fark})`
      : `${ad}: +${r.adet} giriş → bakiye ${r.bakiye}`;
    bildir('ok', metin + (r.yeni_takip && r.sayilan === undefined ? ' · ürün yeni takibe alındı, rafta başka adet varsa say' : ''));
    setOkutulan(null); setElle(null);
    yukle();
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  async function dropshipDegistir(u) {
    try { await api('POST', '/raf-stok/dropship', { sku: u.sku, dropship: !u.dropship }); yukle(); }
    catch (e) { bildir('hata', e.message); }
  }

  async function barkodSil(barkod) {
    if (!window.confirm(`${barkod} barkodu silinsin mi? Hareket geçmişi silinmez.`)) return;
    try { await api('DELETE', `/raf-stok/barkod/${encodeURIComponent(barkod)}`); setGecmis(null); yukle(); }
    catch (e) { bildir('hata', e.message); }
  }

  const sayac = useMemo(() => {
    const l = urunler || [];
    return {
      takipte: l.filter(u => u.takipte).length,
      eksi: l.filter(u => u.bakiye < 0).length,
      dropship: l.filter(u => u.dropship).length,
      tumu: l.length,
    };
  }, [urunler]);

  const liste = useMemo(() => {
    let l = urunler || [];
    if (filtre === 'takipte') l = l.filter(u => u.takipte);
    if (filtre === 'eksi') l = l.filter(u => u.bakiye < 0);
    if (filtre === 'dropship') l = l.filter(u => u.dropship);
    const t = ara.trim().toLowerCase();
    if (t) l = l.filter(u => u.name?.toLowerCase().includes(t) || u.sku.includes(t) || u.offer_ids.some(o => o.toLowerCase().includes(t)));
    return l;
  }, [urunler, filtre, ara]);

  const alim = useMemo(() => (urunler || []).filter(u => u.bakiye < 0)
    .sort((a, b) => (a.dropship === b.dropship ? a.bakiye - b.bakiye : a.dropship ? -1 : 1)), [urunler]);

  if (!urunler) return <div className="p-10 flex justify-center"><Spinner /></div>;

  return (
    <div className="p-4 md:p-6 space-y-4">
      {kamera && <CameraScanner onClose={() => setKamera(false)} onScan={k => { setKamera(false); okut(k); }} />}
      {elle && <ElleModal urun={elle.urun} mod={elle.mod} onKapat={() => setElle(null)} onBitti={bitti} />}
      {gecmis && <GecmisModal urun={gecmis} onKapat={() => setGecmis(null)} onBarkodSil={barkodSil} />}

      <p className="text-xs text-gray-500">
        Rafta gerçekten duran adet. Ozon'a girilen stoktan bağımsızdır, Ozon'a hiçbir şey göndermez.
        Takipteki ürünlerde her yeni sipariş bakiyeden düşülür; kargoya çıkmadan iptal olursa geri eklenir.
      </p>
      {hata && <p className="text-sm text-red-600">{hata}</p>}

      {/* Barkod okutma */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            {[['giris', '📥 Giriş'], ['sayim', '🔢 Sayım']].map(([k, ad]) => (
              <button key={k} onClick={() => { setMod(k); setOkutulan(o => (o?.tanim ? null : o)); inputRef.current?.focus(); }}
                className={`px-4 py-2 font-medium ${mod === k ? (k === 'giris' ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white') : 'bg-white text-gray-600'}`}>
                {ad}
              </button>
            ))}
          </div>
          <input ref={inputRef} autoFocus value={barkodGir} onChange={e => setBarkodGir(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && okut(barkodGir)}
            placeholder="Barkodu okut (ürün veya koli)…"
            className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <button onClick={() => setKamera(true)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">📷 Kamera</button>
        </div>
        {bildirim && (
          <div className={`text-sm rounded-lg px-3 py-2 border ${bildirim.tip === 'ok' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-red-50 border-red-300 text-red-700'}`}>
            {bildirim.metin}
          </div>
        )}
        {okutulan?.tanimsiz && (
          <BarkodTanimla barkod={okutulan.tanimsiz} urunler={urunler}
            onIptal={() => setOkutulan(null)}
            onKaydet={(b, urun) => { yukle(); setOkutulan({ tanim: { ...b, name: urun.name } }); }} />
        )}
        {okutulan?.tanim && (
          <IslemFormu key={`${okutulan.tanim.barkod}-${mod}`} mod={mod} tanim={okutulan.tanim}
            onBitti={bitti} onIptal={() => { setOkutulan(null); inputRef.current?.focus(); }} />
        )}
      </div>

      {/* Görünüm seçimi */}
      <div className="flex gap-2">
        <button onClick={() => setGorunum('stok')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${gorunum === 'stok' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-600'}`}>
          Raf stoku
        </button>
        <button onClick={() => setGorunum('alim')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${gorunum === 'alim' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-600'}`}>
          🛒 Alım listesi {alim.length > 0 && <span className="ml-1 bg-red-500 text-white rounded-full px-2 text-xs">{alim.length}</span>}
        </button>
        <button onClick={() => setGorunum('barkod')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${gorunum === 'barkod' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-600'}`}>
          🏷️ Barkodlar
        </button>
      </div>

      {gorunum === 'stok' && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="p-3 border-b flex flex-wrap items-center gap-2">
            {[['takipte', 'Takipte'], ['eksi', 'Eksidekiler'], ['dropship', 'Drop-ship'], ['tumu', 'Tüm ürünler']].map(([k, ad]) => (
              <button key={k} onClick={() => setFiltre(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${filtre === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                {ad} ({sayac[k]})
              </button>
            ))}
            <input value={ara} onChange={e => setAra(e.target.value)} placeholder="Ara…"
              className="ml-auto border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48" />
          </div>
          {liste.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">
              {filtre === 'takipte' && sayac.takipte === 0
                ? 'Henüz takipte ürün yok. Bir ürünü saydığında, giriş yaptığında ya da drop-ship işaretlediğinde takibe girer. "Tüm ürünler"den başlayabilirsin.'
                : 'Ürün yok.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Ürün</th>
                    <th className="text-right px-3 py-2">Raf bakiyesi</th>
                    <th className="text-right px-3 py-2" title="Bakiyeden düşülmüş, ürünü henüz gitmemiş siparişler">Bekleyen sipariş</th>
                    <th className="text-left px-3 py-2">Son sayım</th>
                    <th className="text-center px-3 py-2">Drop-ship</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {liste.map(u => (
                    <tr key={u.sku} className={u.bakiye < 0 ? 'bg-red-50/40' : ''}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {u.image_url && <img src={u.image_url} alt="" className="w-9 h-9 object-cover rounded" />}
                          <div className="min-w-0">
                            <p className="text-gray-900 truncate max-w-md">{u.name}</p>
                            <p className="text-xs text-gray-400 truncate">{u.offer_ids.join(' · ')} · SKU {u.sku}
                              {u.barkodlar.length > 0 && ` · ${u.barkodlar.length} barkod`}</p>
                          </div>
                        </div>
                      </td>
                      <td className={`px-3 py-2 text-right text-base font-semibold ${!u.takipte ? 'text-gray-300' : u.bakiye < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {u.takipte ? u.bakiye : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600">
                        {u.bekleyen_siparis > 0 ? `${u.bekleyen_adet} adet / ${u.bekleyen_siparis}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {u.son_sayim ? <>{formatDate(u.son_sayim)}<br />{u.son_sayilan} sayıldı</> : '—'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={u.dropship} onChange={() => dropshipDegistir(u)} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <button onClick={() => setElle({ urun: u, mod: 'sayim' })} className="text-xs text-indigo-600 px-2 py-1 hover:underline">Say</button>
                        <button onClick={() => setElle({ urun: u, mod: 'giris' })} className="text-xs text-green-700 px-2 py-1 hover:underline">Giriş</button>
                        {u.takipte && <button onClick={() => setGecmis(u)} className="text-xs text-gray-600 px-2 py-1 hover:underline">Geçmiş</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {gorunum === 'barkod' && <BarkodEslestirme urunler={urunler} onDegisti={yukle} onSil={barkodSil} />}

      {gorunum === 'alim' && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="p-3 border-b flex items-center gap-2">
            <p className="text-sm text-gray-600 flex-1">
              Raf bakiyesi eksiye inen ürünler. Eksik adet, gelen siparişleri karşılamak için alınması gereken miktardır.
            </p>
            <button onClick={() => csvIndir(alim)} disabled={!alim.length}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm disabled:opacity-40">⬇ CSV</button>
          </div>
          {alim.length === 0 ? <p className="p-6 text-sm text-gray-500 text-center">Eksiye inen ürün yok.</p> : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr><th className="text-left px-3 py-2">Tür</th><th className="text-left px-3 py-2">Ürün</th>
                  <th className="text-right px-3 py-2">Eksik adet</th><th className="text-right px-3 py-2">Bekleyen sipariş</th><th></th></tr>
              </thead>
              <tbody className="divide-y">
                {alim.map(u => (
                  <tr key={u.sku}>
                    <td className="px-3 py-2">
                      <span className={`text-xs rounded px-2 py-0.5 ${u.dropship ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                        {u.dropship ? 'Drop-ship' : 'Raf'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-gray-900">{u.name}</p>
                      <p className="text-xs text-gray-400">{u.offer_ids.join(' · ')} · SKU {u.sku}</p>
                    </td>
                    <td className="px-3 py-2 text-right text-lg font-bold text-red-600">{-u.bakiye}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{u.bekleyen_siparis || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setElle({ urun: u, mod: 'giris' })} className="text-xs text-green-700 hover:underline">Alım geldi</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
